import React, { useState, useEffect } from 'react';
import { IonToast, IonButton, IonIcon } from '@ionic/react';
import { downloadOutline, closeOutline, refreshOutline } from 'ionicons/icons';
import { checkAndUpdateData } from '../data/dataApi';
import { Storage } from '@capacitor/storage';

interface VersionUpdateNotificationProps {
  // Props opcionales para personalizar el comportamiento
  autoShow?: boolean;
  position?: 'top' | 'bottom' | 'middle';
  duration?: number;
}

const VersionUpdateNotification: React.FC<VersionUpdateNotificationProps> = ({
  autoShow = true,
  position = 'top',
  duration = 0 // 0 = no se cierra automáticamente
}) => {
  const [showToast, setShowToast] = useState(false);
  const [newVersion, setNewVersion] = useState<string>('');
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (!autoShow) return;

    // Listener para el evento de actualización disponible
    const handleVersionUpdate = (event: CustomEvent<{ newVersion: string; isManual: boolean }>) => {
      const { newVersion: version, isManual } = event.detail;
      
      // Solo mostrar para verificaciones automáticas
      if (!isManual) {
        setNewVersion(version);
        setShowToast(true);
      }
    };

    window.addEventListener('versionUpdateAvailable', handleVersionUpdate);

    return () => {
      window.removeEventListener('versionUpdateAvailable', handleVersionUpdate);
    };
  }, [autoShow]);

  const handleUpdateNow = async () => {
    try {
      setIsUpdating(true);
      
      // Obtener código de evento
      const { value: eventCode } = await Storage.get({ key: 'eventcode' });
      
      if (!eventCode || eventCode === 'nada') {
        console.error('No hay código de evento para actualizar');
        return;
      }

      // Actualizar datos
      const updated = await checkAndUpdateData(eventCode, true);
      
      if (updated) {
        setShowToast(false);
        
        // Mostrar toast de éxito
        const successToast = document.createElement('ion-toast');
        successToast.message = `✅ Agenda actualizada a la versión ${newVersion}`;
        successToast.duration = 3000;
        successToast.position = 'top';
        successToast.color = 'success';
        document.body.appendChild(successToast);
        successToast.present();
        
        // Opcional: Recargar la página para asegurar que todos los componentes usen los nuevos datos
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (error) {
      console.error('Error al actualizar:', error);
      
      // Mostrar toast de error
      const errorToast = document.createElement('ion-toast');
      errorToast.message = '❌ Error al actualizar la agenda';
      errorToast.duration = 3000;
      errorToast.position = 'top';
      errorToast.color = 'danger';
      document.body.appendChild(errorToast);
      errorToast.present();
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDismiss = () => {
    setShowToast(false);
  };

  return (
    <IonToast
      isOpen={showToast}
      onDidDismiss={handleDismiss}
      position={position}
      duration={duration}
      color="primary"
      message={`Nueva versión disponible: v${newVersion}`}
      buttons={[
        {
          side: 'start',
          icon: downloadOutline,
          text: isUpdating ? 'Actualizando...' : 'Actualizar',
          handler: handleUpdateNow
        },
        {
          side: 'end',
          icon: closeOutline,
          text: 'Después',
          role: 'cancel',
          handler: handleDismiss
        }
      ]}
      cssClass="version-update-toast"
    />
  );
};

export default VersionUpdateNotification;

// Hook personalizado para usar el sistema de notificaciones de versión
export const useVersionNotification = () => {
  const [hasUpdate, setHasUpdate] = useState(false);
  const [newVersion, setNewVersion] = useState<string>('');

  useEffect(() => {
    const handleVersionUpdate = (event: CustomEvent<{ newVersion: string; isManual: boolean }>) => {
      setHasUpdate(true);
      setNewVersion(event.detail.newVersion);
    };

    window.addEventListener('versionUpdateAvailable', handleVersionUpdate);

    return () => {
      window.removeEventListener('versionUpdateAvailable', handleVersionUpdate);
    };
  }, []);

  const checkNow = async (): Promise<boolean> => {
    try {
      const { versionCheckService } = await import('../services/VersionCheckService');
      return await versionCheckService.checkNow();
    } catch (error) {
      console.error('Error al verificar versión:', error);
      return false;
    }
  };

  const updateNow = async (): Promise<boolean> => {
    try {
      const { value: eventCode } = await Storage.get({ key: 'eventcode' });
      
      if (!eventCode || eventCode === 'nada') {
        return false;
      }

      const updated = await checkAndUpdateData(eventCode, true);
      
      if (updated) {
        setHasUpdate(false);
        setNewVersion('');
      }
      
      return updated;
    } catch (error) {
      console.error('Error al actualizar:', error);
      return false;
    }
  };

  const dismissUpdate = () => {
    setHasUpdate(false);
    setNewVersion('');
  };

  return {
    hasUpdate,
    newVersion,
    checkNow,
    updateNow,
    dismissUpdate
  };
};
